import React, { useState } from 'react';

const normalizeConcerns = (concerns = []) => {
  if (!Array.isArray(concerns)) return [];
  return concerns
    .map((item) => {
      if (!item) return null;
      if (typeof item === 'string') {
        return { concern: item, severity: 'medium', suggestion: '' };
      }
      const rawSeverity = String(item?.severity || 'medium').toLowerCase();
      const severity = ['high', 'medium', 'low'].includes(rawSeverity) ? rawSeverity : 'medium';
      return {
        concern: item?.concern || 'Concern requires review',
        severity,
        suggestion: item?.suggestion || '',
      };
    })
    .filter(Boolean);
};

const confidenceLabel = (score = 0) => {
  if (score >= 0.9) return { label: 'High', className: 'confidence--high' };
  if (score >= 0.7) return { label: 'Good', className: 'confidence--good' };
  if (score >= 0.4) return { label: 'Moderate', className: 'confidence--moderate' };
  return { label: 'Low', className: 'confidence--low' };
};

const normalizeInstructionAdherence = (adherence) => {
  if (!adherence || typeof adherence !== 'object') {
    return { status: 'not_provided', notes: '', gaps: [], detailed_checks: [] };
  }
  const rawStatus = String(adherence.status || 'not_provided').toLowerCase();
  const status = ['met', 'partially_met', 'not_met', 'not_provided'].includes(rawStatus)
    ? rawStatus
    : 'not_provided';
  const notes = adherence.notes ? String(adherence.notes) : '';
  const gaps = Array.isArray(adherence.gaps)
    ? adherence.gaps.map((item) => String(item)).filter(Boolean)
    : [];
  const detailedChecks = Array.isArray(adherence.detailed_checks)
    ? adherence.detailed_checks
        .filter((item) => item && typeof item === 'object' && item.requirement)
        .map((item) => ({
          requirement: String(item.requirement),
          status: ['met', 'partially_met', 'not_met'].includes(String(item.status || '').toLowerCase())
            ? String(item.status).toLowerCase()
            : 'partially_met',
          policy_section: item.policy_section ? String(item.policy_section) : '',
          evidence: item.evidence ? String(item.evidence) : '',
          evidence_quote: item.evidence_quote ? String(item.evidence_quote) : '',
          guidance_reference: item.guidance_reference ? String(item.guidance_reference) : '',
          risk_level: ['high', 'medium', 'low'].includes(String(item.risk_level || '').toLowerCase())
            ? String(item.risk_level).toLowerCase()
            : 'medium',
          recommendation: item.recommendation ? String(item.recommendation) : '',
        }))
    : [];
  return { status, notes, gaps, detailed_checks: detailedChecks };
};

const instructionStatusMeta = (status) => {
  if (status === 'met') return { label: 'Met', className: 'badge--low' };
  if (status === 'partially_met') return { label: 'Partially Met', className: 'badge--medium' };
  if (status === 'not_met') return { label: 'Not Met', className: 'badge--high' };
  return { label: 'Not Provided', className: 'badge--low' };
};

const MergeReport = ({ report }) => {
  const [open, setOpen] = useState({ similarities: true, differences: true, concerns: true });
  const confidence = confidenceLabel(report?.confidence_score || 0);
  const concerns = normalizeConcerns(report?.outstanding_concerns || []);
  const validatorCoverage = report?.coverage_metrics?.validator;
  const additionalInstructions = String(report?.additional_instructions || '').trim();
  const instructionAdherence = normalizeInstructionAdherence(
    report?.instruction_adherence || report?.coverage_metrics?.instruction_adherence
  );
  const instructionStatus = instructionStatusMeta(instructionAdherence.status);

  const renderList = (items = []) => (
    <ul className="insight-list">
      {items.map((item, index) => (
        <li key={`${index}-${String(item).slice(0, 20)}`}>{String(item)}</li>
      ))}
    </ul>
  );

  return (
    <div className="merge-report">
      <div className="merge-report__header">
        <h3>Merge Analysis Report</h3>
        <div className={`confidence-pill ${confidence.className}`}>
          {confidence.label} ({Math.round((report?.confidence_score || 0) * 100)}%)
        </div>
      </div>

      <section className="report-section">
        <h4>Executive Summary</h4>
        <p className="summary-text">{report?.merge_summary || 'No summary generated.'}</p>
      </section>

      {additionalInstructions && (
        <section className="report-section">
          <h4>Additional Merge Instructions</h4>
          <p className="summary-text">{additionalInstructions}</p>
        </section>
      )}

      {additionalInstructions && (
        <section className="report-section">
          <h4>Instruction Adherence</h4>
          <span className={`badge ${instructionStatus.className}`}>{instructionStatus.label}</span>
          {instructionAdherence.notes && <p className="summary-text">{instructionAdherence.notes}</p>}
          {instructionAdherence.gaps.length > 0 && (
            <>
              <p className="muted"><strong>Gaps identified:</strong></p>
              {renderList(instructionAdherence.gaps)}
            </>
          )}
          {instructionAdherence.detailed_checks.length > 0 && (
            <div className="concerns-list">
              {instructionAdherence.detailed_checks.map((check, index) => {
                const checkMeta = instructionStatusMeta(check.status);
                return (
                  <article key={`${index}-${check.requirement.slice(0, 24)}`} className="concern-card">
                    <p><strong>{check.requirement}</strong></p>
                    <span className={`badge ${checkMeta.className}`}>{checkMeta.label}</span>
                    {check.risk_level && <p className="muted"><strong>Risk:</strong> {check.risk_level}</p>}
                    {check.policy_section && <p className="muted"><strong>Policy Section:</strong> {check.policy_section}</p>}
                    {check.guidance_reference && <p className="muted"><strong>Guidance:</strong> {check.guidance_reference}</p>}
                    {check.evidence && <p className="muted"><strong>Evidence:</strong> {check.evidence}</p>}
                    {check.evidence_quote && <p className="muted"><strong>Evidence Quote:</strong> "{check.evidence_quote}"</p>}
                    {check.recommendation && <p className="muted"><strong>Recommendation:</strong> {check.recommendation}</p>}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}

      {(report?.template_summary || report?.practice_a_summary || report?.practice_b_summary) && (
        <section className="report-section">
          <h4>Source Summaries</h4>
          <div className="source-grid">
            {report?.template_summary && (
              <article className="source-card"><strong>Template</strong><p>{report.template_summary}</p></article>
            )}
            {report?.practice_a_summary && (
              <article className="source-card"><strong>Practice A</strong><p>{report.practice_a_summary}</p></article>
            )}
            {report?.practice_b_summary && (
              <article className="source-card"><strong>Practice B</strong><p>{report.practice_b_summary}</p></article>
            )}
          </div>
        </section>
      )}

      {Array.isArray(report?.similarities) && report.similarities.length > 0 && (
        <section className="report-section">
          <button
            className="section-toggle"
            onClick={() => setOpen({ ...open, similarities: !open.similarities })}
            aria-expanded={open.similarities}
            aria-controls="merge-report-similarities"
          >
            Common Ground ({report.similarities.length})
          </button>
          {open.similarities && <div id="merge-report-similarities">{renderList(report.similarities)}</div>}
        </section>
      )}

      {Array.isArray(report?.key_differences) && report.key_differences.length > 0 && (
        <section className="report-section">
          <button
            className="section-toggle"
            onClick={() => setOpen({ ...open, differences: !open.differences })}
            aria-expanded={open.differences}
            aria-controls="merge-report-differences"
          >
            Key Differences ({report.key_differences.length})
          </button>
          {open.differences && <div id="merge-report-differences">{renderList(report.key_differences)}</div>}
        </section>
      )}

      {concerns.length > 0 && (
        <section className="report-section report-section--concerns">
          <button
            className="section-toggle"
            onClick={() => setOpen({ ...open, concerns: !open.concerns })}
            aria-expanded={open.concerns}
            aria-controls="merge-report-concerns"
          >
            Outstanding Concerns ({concerns.length})
          </button>
          {open.concerns && (
            <div id="merge-report-concerns" className="concerns-list">
              {concerns.map((concern, index) => (
                <article key={`${index}-${concern.concern.slice(0, 20)}`} className="concern-card">
                  <span className={`badge badge--${concern.severity}`}>{concern.severity}</span>
                  <p>{concern.concern}</p>
                  {concern.suggestion && <p className="muted"><strong>Suggestion:</strong> {concern.suggestion}</p>}
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {validatorCoverage && (
        <section className="report-section">
          <h4>Coverage Metrics</h4>
          <p className="summary-text">
            Merged words: <strong>{validatorCoverage.merged_word_count || 0}</strong> | Longest source words:{' '}
            <strong>{validatorCoverage.longest_source_word_count || 0}</strong> | Coverage score:{' '}
            <strong>{Math.round((validatorCoverage.overall_coverage_score || 0) * 100)}%</strong>
          </p>
          {validatorCoverage.warning_reason && <p className="muted">{validatorCoverage.warning_reason}</p>}
        </section>
      )}

      {Array.isArray(report?.recommendations) && report.recommendations.length > 0 && (
        <section className="report-section">
          <h4>Recommendations</h4>
          {renderList(report.recommendations)}
        </section>
      )}
    </div>
  );
};

export default MergeReport;
